// Import reactive variable to get currency from shop settings
import { SiteSettingsVar } from '../../lib/reactiveVars'
import { getPriceFromPriceCategories } from '../../features/getCategoryPrice'

// Helper function to get currency from shop settings
const getCurrencyFromSettings = () => {
  const siteSettings = SiteSettingsVar()
  console.log('Site Settings:', siteSettings?.currency, 'EUR')
  return siteSettings?.currency || 'EUR' // Fallback to EUR if no currency is set
}

// Export function to get currency for use in components
export const getCurrency = () => {
  return getCurrencyFromSettings()
}

// ===== format the price data, this function gets currency from ShopSetting automatically =======
export const priceFormatter = price => {
  if (price === null || price === undefined) return ''

  const currency = getCurrencyFromSettings()

  let locale = 'lt-LT' // default locale

  // Use Polish locale for PLN currency to get "zł" symbol
  if (currency === 'PLN') {
    locale = 'pl-PL'
  }

  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  })

  const formattedPrice = formatter.format(price)

  return formattedPrice
}

//* ====================================================================================================================================
//* ================================================ Custom price (entered by the admin) ===============================================
//* ====================================================================================================================================
// FEATURE FLAG. Off unless the variable is literally the string 'true' - the
// same read as REACT_APP_FEATURE_CONVERT_OFFER_TO_ORDER in
// containers/CommercialOffers/CommercialOffers.tsx. CRA inlines this at build
// time, so it is a compile time constant, not runtime state.
//
// This module is the CHOKE POINT for the whole feature: the two predicates that
// decide whether a hand typed price applies at all (`getComponentPriceOverride`
// and `isWholeItemCustomPriceActive`) return their neutral value when the flag
// is off. Every price on every screen, plus the saved cart item, is therefore
// the plain calculated one even if a reactive var still holds a stale override
// (e.g. a cart persisted by a flag-on build). The individual call sites gate
// again on top of this - belt and braces, since the flag-off build must be
// identical to the pre-feature build.
export const CUSTOM_PRICE_ENABLED =
  process.env.REACT_APP_FEATURE_CUSTOM_PRICE === 'true'

// Upper bound for a hand typed custom price, in the shop currency.
// WHY 10 000 000: the most expensive single configured item we sell (a bespoke
// modular sofa in the top fabric category) is in the low tens of thousands, so
// ten million leaves ~100x head room while still being an obvious typo catch.
// It also keeps every offer total far away from the float precision cliff -
// even a hundred such items summed in the PDF stay well inside the range where
// cents are still represented exactly (Number.MAX_SAFE_INTEGER / 100).
export const MAX_CUSTOM_PRICE = 10000000

// The ONLY shape a custom price may be typed in: up to 7 whole digits and at
// most 2 decimals (a comma is accepted too, LT / PL keyboards use it).
//
// INVARIANT - do not widen either side without re-checking the other:
//   the largest string this pattern accepts is "9999999.99" < MAX_CUSTOM_PRICE,
//   so EVERY string it permits parses to a finite number that
//   `isValidCustomPrice` accepts. The two can therefore never disagree, which
//   is what used to let the field show a value the cart silently ignored.
//
// The 7 digit cap kills `Number(...) === Infinity` (needs ~309 digits) and the
// exponential `toString()` at the top end (>= 1e21). The 2 decimal cap kills
// the same exponential problem at the BOTTOM end: JS renders anything below
// 1e-6 as "1e-7", which this very pattern would then reject on every keystroke
// and freeze the field. With 2 decimals the smallest non zero value is 0.01.
export const CUSTOM_PRICE_INPUT_PATTERN = /^\d{0,7}([.,]\d{0,2})?$/

// A custom price is only usable when it is a real, non negative number within
// the supported range. null / undefined / NaN all mean "no custom price".
// This is the SINGLE source of truth for that check - do not inline it again,
// the saved `price` and `custom_price` must always agree on it.
export const isValidCustomPrice = value =>
  typeof value === 'number' &&
  isFinite(value) &&
  value >= 0 &&
  value <= MAX_CUSTOM_PRICE

// Parses the raw text of a custom price field.
// Empty / whitespace means "no custom price" (NOT 0), so it returns null.
// Shared by the whole-item field and by every per-component field so they
// cannot drift apart - there are N of these inputs on screen now, not one.
export const parseCustomPriceInput = value => {
  if (value === null || value === undefined) {
    return null
  }
  const stringValue = value.toString().trim()
  if (stringValue === '') {
    return null
  }
  const parsedValue = Number(stringValue.replace(',', '.'))
  if (isNaN(parsedValue) || parsedValue < 0) {
    return null
  }
  return parsedValue
}

// Seeds a text field from a stored custom price.
// Relies on CUSTOM_PRICE_INPUT_PATTERN keeping every stored value inside
// [0.01 .. 9999999.99] (plus 0), where `toString()` never goes exponential -
// an "1e-7" or "1e+21" here would be rejected by the field's own pattern on the
// next keystroke and freeze it.
export const formatCustomPriceForInput = value =>
  value === null || value === undefined ? '' : value.toString()

// Resolves the price that will actually be used for the cart item.
// A valid custom price REPLACES the calculated one.
// Returns null when neither is known yet (the configuration is still being
// priced). Callers that do arithmetic must coalesce with `?? 0` themselves -
// display code MUST NOT, `priceFormatter(null)` renders '' on purpose so a
// pending calculation stays blank instead of printing a confident "0,00 EUR".
export const resolveFinalPrice = (customPrice, calculatedPrice) => {
  if (isValidCustomPrice(customPrice)) {
    return customPrice
  }
  return calculatedPrice ?? null
}

//* ====================================================================================================================================
//* ============================== Additional component pricing (ONE definition, used everywhere) ======================================
//* ====================================================================================================================================
// "What does this additional component cost?" used to be copy/pasted in six
// places (two loops in AdvancedProductConfigurationPreview, two in
// SofaConfigurationPreview, the selection card in AdditionalComponentGroup and
// the review card in PreviewSelectedAdditionalComponent). They were all
// behaviourally identical, so they are all routed through this one function -
// the summed total and every displayed price now cannot disagree.
//
// DELIBERATELY UNNORMALISED: this returns EXACTLY what the inlined branches
// returned, including `undefined` / `null`.
//   * getPriceFromPriceCategories returns null on three early exits and
//     `undefined` from `currentPriceCategory?.price`.
//   * The sum sites do `total + value`; `+ null` is a no-op but `+ undefined`
//     is NaN. That asymmetry is PRE-EXISTING.
// Coalescing here (e.g. `?? 0`) would silently change what four screens print,
// which is exactly what a refactor must not do. Each call site keeps its own
// post-processing (the isNaN->null mapping and the `!= null` row gate in
// PreviewSelectedAdditionalComponent, the bare priceFormatter in the selection
// card, the `+` coercion in the sums).
export const getComponentCalculatedPrice = (
  component,
  selectedAdvancedProductFabric
) => {
  // 1) Component will either have extra_price
  // 2) Or a PriceFabricCategory - price depending on the selected Fabric.
  if (
    component.use_fabric_prices_for_components &&
    component.price_fabric_category &&
    component.price_fabric_category.length &&
    selectedAdvancedProductFabric?.fabricGroupObject?.form_price_fabric_category
  ) {
    return getPriceFromPriceCategories(
      selectedAdvancedProductFabric,
      component.price_fabric_category
    )
  }
  return component.extra_price
}

// The key a per-component price override is stored under.
// Always a STRING: the map is persisted on the cart object and survives a
// JSON round trip, which turns numeric keys into strings anyway. Reading and
// writing through this one function is what guarantees the seeded map from
// `cart-edit-utils` lines up with what `onSubmit` wrote.
export const getComponentPriceKey = component => {
  const id = component?.id
  if (id === null || id === undefined) return null
  return String(id)
}

// Returns the admin typed price for this component, or null when there is none.
// `isValidCustomPrice` is the gate, so a deliberate 0 is a REAL override and is
// returned as 0 (never treated as "empty").
//
// FLAG OFF => always null, whatever the var holds. This single line is what
// makes getComponentEffectivePrice collapse to getComponentCalculatedPrice (the
// exact pre-feature expression) at all six price sites, and makes
// buildComponentPriceOverrides return an empty map.
export const getComponentPriceOverride = (component, overrides) => {
  if (!CUSTOM_PRICE_ENABLED) return null
  const key = getComponentPriceKey(component)
  if (key === null || !overrides) return null
  const value = overrides[key]
  return isValidCustomPrice(value) ? value : null
}

// The price this component actually contributes / displays.
// A per-component override REPLACES the calculated price. When there is no
// override the calculated value is passed through untouched (see the
// "deliberately unnormalised" note above).
export const getComponentEffectivePrice = (
  component,
  selectedAdvancedProductFabric,
  overrides
) => {
  const override = getComponentPriceOverride(component, overrides)
  if (override !== null) return override
  return getComponentCalculatedPrice(component, selectedAdvancedProductFabric)
}

// Drops override entries whose component is no longer selected.
// Selection is exclusive per group, so switching from "Mattress A" to
// "Mattress B" must not leave A's price parked in the map - it would come back
// to life (and back into the total) the moment the admin switched back.
// Keeping the map == the selected set is what makes the summed total and the
// displayed inputs structurally unable to diverge.
// Returns null when nothing had to be removed, so callers can skip the write
// and avoid re-rendering every subscriber for no reason.
export const pruneComponentPriceOverrides = (overrides, selectedComponents) => {
  const allowedKeys = {}
  for (const component of selectedComponents ?? []) {
    const key = getComponentPriceKey(component)
    if (key !== null) {
      allowedKeys[key] = true
    }
  }

  const next = {}
  let changed = false
  for (const key of Object.keys(overrides ?? {})) {
    if (Object.prototype.hasOwnProperty.call(allowedKeys, key)) {
      next[key] = overrides[key]
    } else {
      changed = true
    }
  }
  return changed ? next : null
}

// The map that gets persisted on the cart object. Built from the CURRENTLY
// SELECTED components rather than copied wholesale out of the reactive var, so
// the "only selected components" invariant holds at the persistence boundary
// even if the pruning effect has not flushed yet.
export const buildComponentPriceOverrides = (overrides, selectedComponents) => {
  const result = {}
  for (const component of selectedComponents ?? []) {
    const key = getComponentPriceKey(component)
    const override = getComponentPriceOverride(component, overrides)
    if (key !== null && override !== null) {
      result[key] = override
    }
  }
  return result
}

// The whole-item "Custom price" field is a FALLBACK ONLY: it applies when the
// item has no selected additional components, i.e. when there are no
// per-component override rows to type into.
//
// This is the SINGLE definition of that rule, shared by the display
// (ConfigurationBlock, StickyPricePreview) and by AdvancedCartItemForm.onSubmit.
// It is what makes the two discount rules structurally exclusive: a whole item
// custom price forces `discount_percentage: null`, per-component overrides
// leave the inherited discount alone, and this predicate can only be true when
// there are zero selected components (hence zero possible overrides).
//
// Gating in code rather than relying on "the input is not rendered" matters:
// `customPriceVar` is also seeded from a stored `custom_price` by
// cart-edit-utils, so it can be non-null while the field is invisible.
//
// FLAG OFF => always false. StickyPricePreview / ConfigurationBlock then show
// advancedProductTotalPriceVar unchanged and AdvancedCartItemForm.onSubmit
// takes its pre-feature `price` / `discount_percentage` branch.
export const isWholeItemCustomPriceActive = (customPrice, selectedComponents) =>
  CUSTOM_PRICE_ENABLED &&
  isValidCustomPrice(customPrice) &&
  (selectedComponents?.length ?? 0) === 0

export const discountSubtractor = (price, discountPercentage) => {
  if (discountPercentage) {
    const fullPrice = price
    const discountAmount = price * (discountPercentage / 100)
    const priceAfterDiscont = fullPrice - discountAmount
    return priceAfterDiscont
  } else {
    return price
  }
}

// Accepts a discount and returns true / false if discount is good to applu
export const discountCheckUtility = discountObject => {
  let check = true
  if (discountObject) {
    // If there is a discount we check if its active.
    if (discountObject.active) {
      // If its active we check if the start date has already passed.
      if (discountObject.startDate) {
        if (new Date(discountObject.startDate).getTime() > Date.now()) {
          check = false
        }
      }
      // If its active we check if the end date has already passed.
      if (discountObject.expiryDate) {
        if (new Date(discountObject.expiryDate).getTime() < Date.now()) {
          check = false
        }
      }
    }
  } else {
    return false
  }
  return check
}

//* ====================================================================================================================================
//* ================================================ Calculate discounted product container ============================================
//* ====================================================================================================================================
//These are for DISCOUNTS, which are always in percentage (not to confuse with coupons, which can be fixed or percentage)
export const getDiscountedProductPrice = (price, percentageDiscount) => {
  // TODO: Use the same pattern as getCurrencyFromSettings() to get VAT settings from SiteSettingsVar
  // const siteSettings = SiteSettingsVar()
  // const current_prices_with_vat = siteSettings?.current_prices_with_vat ?? true
  // const vat_percentage = siteSettings?.vat_percentage ?? 21

  const current_prices_with_vat = true
  const vat_percentage = 21

  let discountedPrice = null

  if (current_prices_with_vat) {
    let priceWithoutVat = calculatePriceWithoutVat(price, vat_percentage)
    let discountedPriceWithouVat =
      (priceWithoutVat * (100 - percentageDiscount)) / 100
    discountedPrice = calculatePriceWithVat(
      discountedPriceWithouVat,
      vat_percentage
    )
  } else {
    discountedPrice = (price * (100 - percentageDiscount)) / 100
  }

  return discountedPrice
}

export const getPercentageDiscountAmount = (price, percentageDiscount) => {
  let discountedAmount = (price * percentageDiscount) / 100

  return discountedAmount
}

//* ====================================================================================================================================
//* ================================================ Calculate total_price_without_VAT =================================================
//* ====================================================================================================================================
export const calculatePriceWithoutVat = (price, VAT) => {
  const priceWithoutVat = price / ((100 + VAT) / 100)
  return priceWithoutVat
}

//* ==========================================================================================================================================
//* ====================================================== Calculate price WITH VAT ==========================================================
//* ==========================================================================================================================================
export const calculatePriceWithVat = (price, VAT) => {
  const amountToAdd = price * (VAT / 100)
  const priceWithVat = price + amountToAdd

  return priceWithVat
}

//* =================================================================================================================================================
//* ====================================================== Calculate VAT amount from price ==========================================================
//* =================================================================================================================================================
//This function gives back the amount that should be added on top of the given price without VAT (e.g. given the subtotal without VAT, we want to display how much VAT will be added on top to get the total price)
export const calculateVatAmount = (priceWithoutVat, VAT) => {
  return priceWithoutVat * (VAT / 100)
}
